import { StudyConfig } from '@csp/csp-common-config';
import { ContentUtil } from '@csp/csp-common-content';
import {
  BioSample,
  BioSampleCode,
  BioSampleCodeFormatType,
  BioSampleCodeInputError,
  BioSampleKitType,
  CollectionStatusValueType,
  ProcessingStatusValueType,
  ShippingStatusValueType,
  VisitHbsKitConfig,
} from '@csp/csp-common-hbs-model';
import { Maybe, Patient, Site, ZonedDateTime } from '@csp/csp-common-model';
import { WebHcpHbsInputErrorEntry } from '@csp/csp-web-content-definitions';
import { BorderColor } from '@csp/csp-web-ui';
import { BioSampleItem } from '../model/BioSampleItem';
import { SampleItemStateType } from '../type/SampleItemStateType';
import { SamplesSelectType } from '../type/SamplesSelectType';
import { PatientPermissionService } from '../../security/service/PatientPermissionService';
import { BioSampleContentService } from '../service/BioSampleContentService';
import { CollectBioSampleItem } from '../hbs-collection/model/CollectBioSampleItem';
import { AliquotBioSampleItem } from '../hbs-aliquot/model/AliquotBioSampleItem';

const validateSampleCode = (
  sampleCode: BioSampleCode,
  studyConfig: StudyConfig,
  hbsTexts: WebHcpHbsInputErrorEntry,
): Maybe<BioSampleCodeInputError> =>
  studyConfig.hbsConfig.bioSampleCodeFormatter.validateBioSampleCode(sampleCode, {
    [BioSampleCodeFormatType.LABCORP]: ContentUtil.mapEnum(
      BioSampleCodeFormatType.LABCORP,
      hbsTexts.sampleCodeInputError,
    ),
  });

const isAllowedKit = (kitType: Maybe<BioSampleKitType>, config: Maybe<VisitHbsKitConfig>): boolean =>
  !kitType || !config?.kitTypes.length || !!config?.kitTypes.includes(kitType);

const isDuplicate = (newSampleCode: BioSampleCode, currentSampleCodes: BioSampleCode[]): boolean =>
  currentSampleCodes.some(sampleCode => sampleCode === newSampleCode);

const findDuplicatedSample = <TBioSampleItem extends BioSampleItem>(
  newSampleCode: BioSampleCode,
  currentSamples: TBioSampleItem[],
): Maybe<TBioSampleItem> => currentSamples.find(sample => sample.sampleCode === newSampleCode);

const isEndTimestampBeforeStartTimestamp = (
  startTimestamp: Maybe<ZonedDateTime>,
  endTimestamp: Maybe<ZonedDateTime>,
): Maybe<boolean> => endTimestamp && startTimestamp && endTimestamp.unixTimeMillis < startTimestamp.unixTimeMillis;

const isProcessable = (sample: BioSampleItem): boolean =>
  sample.sampleStatus.value === CollectionStatusValueType.COLLECTED ||
  sample.sampleStatus.value === ProcessingStatusValueType.IN_PROCESS;

const evalIsAliquotable = (sample: BioSample, patient: Patient): boolean =>
  (sample.sampleStatus.value === CollectionStatusValueType.COLLECTED ||
    sample.sampleStatus.value === ProcessingStatusValueType.IN_PROCESS ||
    sample.sampleStatus.value === ProcessingStatusValueType.PROCESSED ||
    sample.sampleStatus.value === ShippingStatusValueType.USED ||
    sample.sampleStatus.value === ShippingStatusValueType.SHIPPED) &&
  sample.collectionStatus.value !== CollectionStatusValueType.ALIQUOTED &&
  sample.collectionStatus.value !== CollectionStatusValueType.NOT_COLLECTED &&
  PatientPermissionService.canManageHbs(patient);

const evalBorderColor = (sampleState: SampleItemStateType): Maybe<BorderColor> => {
  switch (sampleState) {
    case SampleItemStateType.COMPLETE:
      return 'success';
    case SampleItemStateType.ERROR:
      return 'error';
    case SampleItemStateType.WARNING:
      return 'warning';
    case SampleItemStateType.VERIFYING:
      return 'info';
    default:
      return undefined;
  }
};

const evalSelectedState = (samples: { selected: boolean; isNotCollected: boolean }[]): SamplesSelectType => {
  const samplesToUse = samples.filter(sample => !sample.isNotCollected);
  const selectedSamples = samplesToUse.filter(sample => sample.selected);

  if (samplesToUse.length && selectedSamples.length === samplesToUse.length) {
    return SamplesSelectType.ALL;
  } else if (selectedSamples.length) {
    return SamplesSelectType.SOME;
  } else {
    return SamplesSelectType.NONE;
  }
};

const evalKitTypeAlert = (
  kitType: Maybe<BioSampleKitType>,
  kitConfig: Maybe<VisitHbsKitConfig>,
  message: string,
): Maybe<string> => (!isAllowedKit(kitType, kitConfig) ? message : undefined);

const evalKitTypeAlertExistingSample = (
  kitType: Maybe<BioSampleKitType>,
  kitConfig: Maybe<VisitHbsKitConfig>,
): Maybe<string> =>
  evalKitTypeAlert(
    kitType,
    kitConfig,
    BioSampleContentService.getCommonTexts().alert.alertInfo.sampleWithUnconfiguredKit.text,
  );

const evalKitTypeAlertAddedSample = (
  kitType: Maybe<BioSampleKitType>,
  kitConfig: Maybe<VisitHbsKitConfig>,
): Maybe<string> =>
  evalKitTypeAlert(kitType, kitConfig, BioSampleContentService.getCommonTexts().alert.alertInfo.unconfiguredKit.text);

const isSavedWithProcessingEnd = (sample: Maybe<BioSampleItem>): boolean =>
  !!(sample?.sampleId && sample.processingStatus.endedAt);

const sortBySampleCode = (a: { sampleCode: BioSampleCode }, b: { sampleCode: BioSampleCode }): number =>
  parseInt(a.sampleCode) - parseInt(b.sampleCode);

const isSampleStateWarning = <T extends CollectBioSampleItem | AliquotBioSampleItem>(sample: T): boolean =>
  sample.state !== SampleItemStateType.ERROR &&
  sample.state !== SampleItemStateType.WARNING &&
  !sample.isWarningMessageAccepted;

const isSamplePartOfSiteOrUnknown = <T extends CollectBioSampleItem | AliquotBioSampleItem>(
  sample: T,
  site?: Site,
): boolean =>
  sample.bioSampleContainer?.siteNumber && site?.siteNumber
    ? sample.bioSampleContainer.siteNumber === site.siteNumber
    : true;

const shouldUpdateSampleStatusToWarning = <T extends CollectBioSampleItem | AliquotBioSampleItem>(
  sample: T,
  site?: Site,
): boolean => !isSamplePartOfSiteOrUnknown(sample, site) && isSampleStateWarning(sample);

const updateSampleStatusToWarning = <T extends CollectBioSampleItem | AliquotBioSampleItem>(
  sample: T,
  text: string,
): T => ({
  ...sample,
  state: SampleItemStateType.WARNING,
  warningMessage: text,
  isWarningMessageAccepted: false,
});

export const SampleUtil = {
  validateSampleCode,
  isDuplicate,
  findDuplicatedSample,
  isAllowedKit,
  isProcessable,
  evalIsAliquotable,
  evalBorderColor,
  isEndTimestampBeforeStartTimestamp,
  evalSelectedState,
  evalKitTypeAlertAddedSample,
  evalKitTypeAlertExistingSample,
  isSavedWithProcessingEnd,
  sortBySampleCode,
  shouldUpdateSampleStatusToWarning,
  updateSampleStatusToWarning,
};
